import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { errorText } from '../../utils/errorText';
import { can } from '../../utils/permissions';
import { box, button, input } from '../shared/styles';

/*
 * Who each culture-pipeline stage's tasks go to (GET/PUT /pipeline/owners,
 * routes/pipeline_tasks.py, migration 0076): an owner and a backup per
 * stage, picked from the accounts that record lab work
 * (GET /pipeline/assignable). An owner is emailed the moment an isolate's
 * task reaches their stage; the backup takes the stage while the owner
 * cannot sign in. For pipeline:assign (lab managers, collection managers,
 * system admins); a deep link from anyone else is told so and asks nothing.
 *
 * Below, the one-off catch-up: POST /pipeline/sweep makes the tasks of every
 * isolate registered before the pipeline existed and moves every task on as
 * the records say, emailing nobody.
 */
export default function PipelineOwners() {
  const { t } = useTranslation(['pipeline', 'nav', 'common']);
  const { user } = useAuth() || {};
  // Those who hand tasks out (pipeline:assign) name each stage's owners and
  // run the sweep; the Admin hub's other readers (ops:read with
  // pipeline:read: the read-only executive) see who they are.
  const mayEdit = can(user, 'pipeline:assign');
  const allowed = mayEdit || (can(user, 'ops:read') && can(user, 'pipeline:read'));
  const [stages, setStages] = useState(null);
  const [people, setPeople] = useState([]);
  const [draft, setDraft] = useState({});
  const [saved, setSaved] = useState({});
  const [error, setError] = useState('');
  const [sweep, setSweep] = useState(null);

  const load = useCallback(() => Promise.all([
    api.get('/pipeline/owners').then((r) => {
      setStages(r.data.stages);
      setDraft(Object.fromEntries(r.data.stages.map((s) => [s.stage, {
        owner_id: s.owner_id || '', backup_owner_id: s.backup_owner_id || '' }])));
    }),
    mayEdit ? api.get('/pipeline/assignable').then((r) => setPeople(r.data.users || [])) : null,
  ]).catch((e) => setError(errorText(e))), [mayEdit]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!allowed) {
    return (
      <div style={{ padding: 16 }}>
        <h1>{t('owners.title')}</h1>
        <p role="alert" data-testid="owners-forbidden">{t('owners.forbidden')}</p>
      </div>
    );
  }

  const save = (stage) => {
    const row = draft[stage] || {};
    setError('');
    setSaved((s) => ({ ...s, [stage]: false }));
    api.put(`/pipeline/owners/${stage}`, { owner_id: row.owner_id || null, backup_owner_id: row.backup_owner_id || null })
      .then(() => { setSaved((s) => ({ ...s, [stage]: true })); return load(); })
      .catch((e) => setError(errorText(e)));
  };

  const runSweep = () => {
    setSweep('running');
    setError('');
    api.post('/pipeline/sweep').then((r) => setSweep(r.data)).catch((e) => { setSweep(null); setError(errorText(e)); });
  };

  const choose = (stage, key, value) => setDraft((d) => ({ ...d, [stage]: { ...d[stage], [key]: value } }));
  const picker = (stage, key) => (!mayEdit ? (
    <span data-testid={`owners-${key}-${stage}`}>
      {stages.find((s) => s.stage === stage)?.[key === 'owner_id' ? 'owner_name' : 'backup_owner_name'] || t('owners.none')}
    </span>
  ) : (
    <select value={draft[stage]?.[key] || ''} onChange={(e) => choose(stage, key, e.target.value)}
      data-testid={`owners-${key}-${stage}`} style={{ ...input, minHeight: 36, maxWidth: 260 }}>
      <option value="">{t('owners.none')}</option>
      {people.map((p) => <option key={p.user_id} value={p.user_id}>{p.full_name || p.username}</option>)}
      {/* An owner who can no longer sign in is still named, so it can be replaced knowingly. */}
      {draft[stage]?.[key] && !people.some((p) => p.user_id === draft[stage][key]) && (
        <option value={draft[stage][key]}>{t('owners.cannotSignIn')}</option>
      )}
    </select>
  ));

  return (
    <div style={{ padding: 16, maxWidth: 960 }}>
      <h1>{t('owners.title')}</h1>
      <p style={{ color: 'var(--c-text-muted-fg)' }}>{t('owners.intro')}</p>
      {error && <p role="alert" style={{ color: 'var(--c-danger-fg)' }}>{error}</p>}
      {stages && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }} data-testid="owners-table">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--c-border)' }}>
                {['stage', 'owner', 'backup'].map((c) => (
                  <th key={c} scope="col" style={{ textAlign: 'start', padding: '6px 8px' }}>{t(`owners.${c}`)}</th>
                ))}
                <th scope="col" aria-label={t('action.save')} />
              </tr>
            </thead>
            <tbody>
              {stages.map((s, i) => (
                <tr key={s.stage} data-testid={`owners-row-${s.stage}`} style={{ borderBottom: '1px solid var(--c-border)' }}>
                  <td style={cell}>{i + 1}. {t(`stage.${s.stage}`)}</td>
                  <td style={cell}>
                    {picker(s.stage, 'owner_id')}
                    {s.owner_id && !s.owner_usable && <div style={warn}>{t('owners.cannotSignIn')}</div>}
                  </td>
                  <td style={cell}>
                    {picker(s.stage, 'backup_owner_id')}
                    {s.backup_owner_id && !s.backup_owner_usable && <div style={warn}>{t('owners.cannotSignIn')}</div>}
                  </td>
                  <td style={cell}>
                    {mayEdit && <button type="button" style={button('var(--c-primary)')} onClick={() => save(s.stage)}
                      data-testid={`owners-save-${s.stage}`}>{t('action.save')}</button>}
                    {saved[s.stage] && <span role="status" style={{ fontSize: 13 }}>{t('owners.saved')}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {mayEdit && <section style={{ ...box, marginTop: 24 }}>
        <p style={{ marginTop: 0 }}>{t('owners.sweepHint')}</p>
        <button type="button" style={button('var(--c-primary)')} onClick={runSweep} disabled={sweep === 'running'}
          data-testid="owners-sweep">{sweep === 'running' ? t('owners.sweeping') : t('owners.sweep')}</button>
        {sweep && sweep !== 'running' && (
          <span role="status" data-testid="owners-sweep-result">{t('owners.sweepDone', sweep)}</span>
        )}
      </section>}
    </div>
  );
}

const cell = { padding: '8px', verticalAlign: 'top' };
const warn = { fontSize: 12, color: 'var(--c-warning-fg)', marginTop: 4 };
